import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartApi } from './api';
import { trackAddToCart } from './analytics';

const CartContext = createContext(null);

const CART_ID_KEY = 'ninjathlete_cart_id';

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get or create cart on mount
  useEffect(() => {
    const initCart = async () => {
      const storedCartId = localStorage.getItem(CART_ID_KEY);
      
      if (storedCartId) {
        try {
          const response = await cartApi.get(storedCartId);
          setCart(response.data);
        } catch (error) {
          // Cart not found, create new one
          localStorage.removeItem(CART_ID_KEY);
          createNewCart();
        }
      } else {
        createNewCart();
      }
    };

    initCart();
  }, []);

  const createNewCart = async () => {
    try {
      const response = await cartApi.create();
      setCart(response.data);
      localStorage.setItem(CART_ID_KEY, response.data.id);
    } catch (error) {
      console.error('Failed to create cart:', error);
    }
  };

  const addItem = useCallback(async (productId, quantity = 1, variant = null) => {
    if (!cart) return;

    setLoading(true);
    try {
      const response = await cartApi.addItem(cart.id, productId, quantity, variant);
      setCart(response.data);
      const added = response.data?.items?.find((it) => it.product_id === productId);
      if (added) trackAddToCart({ ...added, quantity });
      setIsOpen(true); // Open cart drawer when item added
    } catch (error) {
      console.error('Failed to add item:', error);
    } finally {
      setLoading(false);
    }
  }, [cart]);

  const updateItem = useCallback(async (productId, quantity, variantId = null) => {
    if (!cart) return;

    setLoading(true);
    try {
      const response = await cartApi.updateItem(cart.id, productId, quantity, variantId);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to update item:', error);
    } finally {
      setLoading(false);
    }
  }, [cart]);

  const removeItem = useCallback(async (productId, variantId = null) => {
    if (!cart) return;

    setLoading(true);
    try {
      const response = await cartApi.removeItem(cart.id, productId, variantId);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to remove item:', error);
    } finally {
      setLoading(false);
    }
  }, [cart]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen(prev => !prev), []);

  const itemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const value = {
    cart,
    isOpen,
    loading,
    itemCount,
    addItem,
    updateItem,
    removeItem,
    openCart,
    closeCart,
    toggleCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
